import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import classes from './LookupsRow.module.scss';

/**
 * "Make URLs dynamic with Lookups" row (prototype: above the step cards).
 *
 * USABILITY-ONLY: lookups are the source-level `dataMapIds` array; selecting/
 * removing writes via `onAPIConfigChanged('dataMapIds', value)` — the same key
 * the legacy form used. Removal of a lookup still referenced by a step URL is
 * blocked with `setFormError`, mirroring the legacy handler.
 */

interface LookupValue {
  label: string;
  value: string | number;
}

export interface LookupsRowProps {
  /** currently selected lookups (source-level dataMapIds) */
  dataMapIds: LookupValue[] | null | undefined;
  /** available data maps (object keyed by id, or array) */
  dataMaps: any;
  /** all iterations, to detect if a lookup is still used in a URL before removal */
  iterations: Record<string, any>[];
  onAPIConfigChanged: (name: string, value: any) => void;
  setFormError: (formType: any, message: string) => void;
}

const FORM_TYPE_CREATE_DATA_SOURCE = 'FORM_TYPE_CREATE_DATA_SOURCE';

const toOptions = (dataMaps: any): LookupValue[] => {
  if (!dataMaps) return [];
  const values = Array.isArray(dataMaps) ? dataMaps : Object.values(dataMaps);
  return values
    .filter(Boolean)
    .map((m: any) => ({ label: m.name, value: m.id }))
    .filter((o: LookupValue) => o.value != null);
};

export const LookupsRow: React.FC<LookupsRowProps> = ({
  dataMapIds,
  dataMaps,
  iterations,
  onAPIConfigChanged,
  setFormError,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const selected: LookupValue[] = dataMapIds ?? [];
  const selectedValues = new Set(selected.map((s) => String(s.value)));
  const available = toOptions(dataMaps).filter((o) => !selectedValues.has(String(o.value)));

  // Keep the row to one line: show a few chips inline, collapse the rest into a
  // "+N more" dropdown.
  const VISIBLE_CHIPS = 3;
  const visibleLookups = selected.slice(0, VISIBLE_CHIPS);
  const overflowLookups = selected.slice(VISIBLE_CHIPS);

  const addLookup = (option: LookupValue) => {
    onAPIConfigChanged('dataMapIds', [...selected, option]);
    setAnchorEl(null);
  };

  const removeLookup = (option: LookupValue) => {
    // Mirror legacy: block removal if the lookup macro is used in any step URL.
    const macro = `{lookup${option.value}.`;
    const usedIn = iterations.find((it) => (it['url.template'] || '').includes(macro));
    if (usedIn) {
      setFormError(
        FORM_TYPE_CREATE_DATA_SOURCE,
        `This lookup is used in the API URL ${usedIn['url.template']}.`,
      );
      return;
    }
    onAPIConfigChanged(
      'dataMapIds',
      selected.filter((s) => String(s.value) !== String(option.value)),
    );
  };

  return (
    <div className={classes.row} data-testid="lookups-row">
      <span className={classes.label}>Make URLs dynamic with Lookups</span>

      {visibleLookups.map((lk) => (
        <span
          className={classes.chip}
          key={String(lk.value)}
          data-testid={`lookup-chip-${lk.value}`}
        >
          <span className={classes.chipText}>
            <span className={classes.chipName}>{lk.label}</span>
            <span className={classes.chipId}>lookup{lk.value}</span>
          </span>
          <button
            type="button"
            className={classes.chipRemove}
            aria-label={`Remove ${lk.label}`}
            onClick={() => removeLookup(lk)}
            data-testid={`lookup-remove-${lk.value}`}
          >
            ×
          </button>
        </span>
      ))}

      {overflowLookups.length > 0 && (
        <button
          type="button"
          className={classes.moreBtn}
          onClick={(e) => setMoreAnchorEl(e.currentTarget)}
          data-testid="lookups-more"
        >
          <span className={classes.moreText}>+{overflowLookups.length} more</span>
          <span className={classes.caret} aria-hidden="true">
            ▾
          </span>
        </button>
      )}

      <button
        type="button"
        className={classes.addBtn}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={available.length === 0}
        data-testid="lookups-add"
      >
        +Add Lookup
      </button>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {available.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            onClick={() => addLookup(opt)}
            sx={{ fontSize: '12px', fontFamily: 'var(--font-family-default, sans-serif)' }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={moreAnchorEl} open={!!moreAnchorEl} onClose={() => setMoreAnchorEl(null)}>
        {overflowLookups.map((lk) => (
          <MenuItem
            key={String(lk.value)}
            disableRipple
            sx={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}
          >
            <span className={classes.menuItemText}>
              <span className={classes.chipName}>{lk.label}</span>
              <span className={classes.chipId}>lookup{lk.value}</span>
            </span>
            <button
              type="button"
              className={classes.chipRemove}
              aria-label={`Remove ${lk.label}`}
              onClick={() => {
                removeLookup(lk);
                setMoreAnchorEl(null);
              }}
              data-testid={`lookup-remove-${lk.value}`}
            >
              ×
            </button>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default LookupsRow;
